import {Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse, HttpResponse} from '@angular/common/http';
import {Observable, of, throwError} from 'rxjs';
import {catchError, map} from 'rxjs/operators';

import {environment} from '@env/environment';
import {TokenService} from '../security/token.service';
import {
    SignificantChangeLoadingConfig,
    SignificantChangesReport
} from '@app/views/admin/application-settings/models/significant-changes.model';
import {SyncHistoryItem} from '@app/views/admin/components/sync-history-timeline/models/sync-history-item.model';
import {ProcessHistoryState} from '@app/views/admin/components/sync-history-timeline/models/process-history-state.model';
import {IntegrationRulesMap} from '@app/core/model/integration-models';

@Injectable()
export class SignificantChangesService {

    public readonly DEFAULT_HISTORY_LIMIT = 20;

    constructor(
        private http: HttpClient,
        public tokenService: TokenService
    ) {

    }

    saveSignificantChangesDecision(integrationId: string, isApplied: boolean): Observable<HttpResponse<string>> {
        const url = `${environment.datahubBaseURI}/api/v2/significant-changes/action?integrationId=${integrationId}&isApplied=${isApplied}`;

        return this.http.post<HttpResponse<string>>(url, null)
            .pipe(
                catchError((response: HttpResponse<string>): Observable<any> => {
                    if (response.status === 200 || response.status === 202) {
                        return of(response);
                    }
                    return throwError(response);
                })
            );
    }

    saveSupportSignificantChangesDecision(districtGuid: string, isApplied: boolean): Observable<HttpResponse<string>> {
        const url = `${environment.datahubBaseURI}/api/v2/significant-changes/support-action?districtGuid=${districtGuid}&isApplied=${isApplied}`;

        return this.http.post<HttpResponse<string>>(url, null)
            .pipe(
                catchError((response: HttpResponse<string>): Observable<any> => {
                    if (response.status === 200 || response.status === 202) {
                        return of(response);
                    }
                    return throwError(response);
                })
            );
    }

    saveSignificantChangesInAccessDecision(integrationId: string, isApplied: boolean): Observable<HttpResponse<string>> {

        return isApplied ?
            this.approveSignificantChangesInAccess(integrationId) :
            this.rejectSignificantChangesInAccess(integrationId);
    }

    approveSignificantChangesInAccess(integrationId: string): Observable<HttpResponse<string>> {
        const url = `${environment.datahubBaseURI}/api/v2/rosterAccess/approve-significant-change/${integrationId}`;

        return this.http.post<HttpResponse<string>>(url, null)
            .pipe(
                catchError((response: HttpResponse<string>): Observable<any> => {
                    if (response.status === 200 || response.status === 202) {
                        return of(response);
                    }
                    return throwError(response);
                })
            );
    }

    rejectSignificantChangesInAccess(integrationId: string): Observable<HttpResponse<string>> {
        const url = `${environment.datahubBaseURI}/api/v2/rosterAccess/reject-significant-change/${integrationId}`;

        return this.http.post<HttpResponse<string>>(url, null)
            .pipe(
                catchError((response: HttpResponse<string>): Observable<any> => {
                    if (response.status === 200 || response.status === 202) {
                        return of(response);
                    }
                    return throwError(response);
                })
            );
    }

    getFinalSignificantChangesDecisionProgress(integrationId: string): Observable<number> {
        const url = `${environment.datahubBaseURI}/api/v2/significant-changes/action-percent/${integrationId}`;

        return this.http.get<number>(url)
            .pipe(
                catchError(this.handleError)
            );
    }

    getSignificantChangesOpenReport(districtGuid: string): Observable<SignificantChangesReport> {
        const url = `${environment.datahubBaseURI}/api/v2/significant-changes/open-report?districtGuid=${districtGuid}`;

        return this.http.get<SignificantChangesReport>(url)
            .pipe(
                map(report => report ? Object.assign(new SignificantChangesReport(), report) : null),
                catchError(this.handleError)
            );
    }

    getSignificantChangesOpenReportByIntegrationId(integrationId: string): Observable<SignificantChangesReport> {
        const url = `${environment.datahubBaseURI}/api/v2/significant-changes/open-report-integration?integrationId=${integrationId}`;

        return this.http.get<SignificantChangesReport>(url)
            .pipe(
                map(report => report ? Object.assign(new SignificantChangesReport(), report) : null),
                catchError(this.handleError)
            );
    }

    getSignificantChangesInAccessOpenReportByIntegrationId(integrationId: string): Observable<SignificantChangesReport> {
        const url = `${environment.datahubBaseURI}/api/v2/rosterAccess/open-report-integration?integrationId=${integrationId}`;

        return this.http.get<SignificantChangesReport>(url)
            .pipe(
                map(report => report ? Object.assign(new SignificantChangesReport(), report) : null),
                catchError(this.handleError)
            );
    }

    getFilterRulesByReportId(reportId: string): Observable<IntegrationRulesMap> {
        const url = `${environment.datahubBaseURI}/api/v2/applications/report/${reportId}/rules`;

        return this.http.get<IntegrationRulesMap>(url)
            .pipe(
                catchError(this.handleError)
            );
    }

    getSignificantChangesAllReports(districtGuid: string, offset: number = 0, limit: number = 20): Observable<SignificantChangesReport[]> {
        const params = {
            districtGuid: districtGuid,
            offset: offset,
            limit: limit
        };

        const args = Object.keys(params)
            .filter((paramName: string) => params[paramName] !== null)
            .map((paramName: string) => `${paramName}=${params[paramName]}`)
            .join('&');

        const url = `${environment.datahubBaseURI}/api/v2/significant-changes/all-reports?${args}`;
        return this.http.get<SignificantChangesReport[]>(url)
            .pipe(
                catchError(this.handleError)
            );
    }

    getSignificantChangesAllReportsByIntegrationId(integrationId: string, offset: number = 0, limit: number = 20): Observable<SignificantChangesReport[]> {
        const params = {
            integrationId: integrationId,
            offset: offset,
            limit: limit
        };

        const args = Object.keys(params)
            .filter((paramName: string) => params[paramName] !== null)
            .map((paramName: string) => `${paramName}=${params[paramName]}`)
            .join('&');

        const url = `${environment.datahubBaseURI}/api/v2/significant-changes/all-reports-integration?${args}`;
        return this.http.get<SignificantChangesReport[]>(url)
            .pipe(
                catchError(this.handleError)
            );
    }

    getProcessedSignificantChangesReports(
        config: SignificantChangeLoadingConfig, significantChangeReportMap: Map<string, SignificantChangesReport> = new Map()
    ): Observable<[SignificantChangeLoadingConfig, Map<string, SignificantChangesReport>]> {
        if (config.isAllSignificantChangesLoaded) {
            return of([config, significantChangeReportMap]);
        } else {
            return this.getSignificantChangesAllReports(
                config.orgGuid, config.offset, config.limit
            ).pipe(map((reports: SignificantChangesReport[]) => {
                if (reports && reports.length > 0) {
                    reports.forEach((report: SignificantChangesReport) => significantChangeReportMap.set(report.sessionId.toString(), report));
                    config.lastReportSessionId = Math.min(reports[reports.length - 1].sessionId, config.lastReportSessionId);
                }

                config.isAllSignificantChangesLoaded = significantChangeReportMap.size < (config.offset + config.limit);
                return [config, significantChangeReportMap];
            }));
        }
    }

    getNextSignificantChangesReports(
        significantChangeReportsLoadingConfig: SignificantChangeLoadingConfig,
        significantChangeReportsMap: Map<string, SignificantChangesReport>
    ): (value: SyncHistoryItem[], index: number) => Observable<[SyncHistoryItem[], Map<string, SignificantChangesReport>]> {
        return (syncHistory: SyncHistoryItem[]): Observable<[SyncHistoryItem[], Map<string, SignificantChangesReport>]> => {
            if (syncHistory && syncHistory[syncHistory.length - 1]
                && syncHistory[syncHistory.length - 1].sessionId < significantChangeReportsLoadingConfig.lastReportSessionId
            ) {
                return of([syncHistory, significantChangeReportsMap]);
            } else {
                significantChangeReportsLoadingConfig.limit = this.DEFAULT_HISTORY_LIMIT;

                return this.getProcessedSignificantChangesReports(significantChangeReportsLoadingConfig, significantChangeReportsMap).pipe(
                    map(([config, reportsMap]: [SignificantChangeLoadingConfig, Map<string, SignificantChangesReport>]) => {
                        significantChangeReportsLoadingConfig = config;
                        significantChangeReportsMap = reportsMap;
                        return [syncHistory, significantChangeReportsMap];
                    })
                );
            }
        };
    }

    refreshLastSignificantChangesReport(
        significantChangeReportsLoadingConfig: SignificantChangeLoadingConfig,
        significantChangeReportsMap: Map<string, SignificantChangesReport>
    ): (value: SyncHistoryItem[], index: number) => Observable<[SyncHistoryItem[], Map<string, SignificantChangesReport>]> {
        return (syncHistory: SyncHistoryItem[]): Observable<[SyncHistoryItem[], Map<string, SignificantChangesReport>]> => {
            const isPreviousItemHasUnresolvedSignificantChanges = syncHistory[1]
                && significantChangeReportsMap.has(syncHistory[1].sessionId.toString())
                && !significantChangeReportsMap.get(syncHistory[1].sessionId.toString()).finalAppliedAction;

            if (syncHistory.length == 0 || syncHistory[0] && syncHistory[0].state === ProcessHistoryState.RUNNING && !isPreviousItemHasUnresolvedSignificantChanges) {
                return of([syncHistory, significantChangeReportsMap]);
            } else {
                significantChangeReportsLoadingConfig.offset = 0;
                significantChangeReportsLoadingConfig.limit = 2;
                significantChangeReportsLoadingConfig.isAllSignificantChangesLoaded = false;

                return this.getProcessedSignificantChangesReports(significantChangeReportsLoadingConfig, significantChangeReportsMap).pipe(
                    map(([config, reportsMap]: [SignificantChangeLoadingConfig, Map<string, SignificantChangesReport>]) => {
                        significantChangeReportsLoadingConfig = config;
                        significantChangeReportsMap = reportsMap;
                        return [syncHistory, significantChangeReportsMap];
                    })
                );
            }
        };
    }


    private getQueryParams(params: Object): string {
        return Object.keys(params)
            .filter((key: string) => params[key])
            .map((key: string) => `${key}=${params[key]}`)
            .join('&');
    }

    private handleError(error: HttpErrorResponse) {
        if (error.error instanceof ErrorEvent) {
            console.error('An error occurred:', error.error.message);
        } else {
            console.error(
                `Backend returned ${error.status}, ${error.error}`);
        }
        return throwError(
            'Something bad happened, please try again later.');
    }
}
